import {HttpErrorResponse} from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {NgForm, Validators} from '@angular/forms';
import {DcpAuthService} from '@app/auth/services/dcp-auth.service';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';
import {SubmitFormButtonDirective} from '@app/directives/analytics/submit-form-button.directive';
import {WhitespaceOnlyDirective} from '@app/directives/validators/whitespace-only.directive';
import {DcpConsentTemplateData} from '@app/model/dcp-consent-template-data';
import {ProductPageDecoratorService} from '@app/product/services/product-page-decorator.service';
import {CampaignTrackingService} from '@app/services/campaign-tracking.service';
import {CountrySelectorService} from '@app/services/country-selector.service';
import {
  cityParameterName,
  companyNameParameterName,
  countryParameterName,
  CrmAccountTrackingService,
  customerIdParameterName,
  postalCodeParameterName,
  streetLine2ParameterName,
  streetParameterName
} from '@app/services/crm-account-tracking.service';
import {DcpKymaService} from '@app/services/dcp-kyma.service';
import {DcpUserService} from '@app/services/user/dcp-user.service';
import {
  CampaignData,
  ConfigService,
  DcpBundleTemplate,
  FeatureTogglesService,
  TransactionListCta,
  TransactionType,
  ProductDetermination
} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {AuthStorageService, Image, Product, User} from '@spartacus/core';
import {LaunchDialogService} from '@spartacus/storefront';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {RecaptchaComponent} from 'ng-recaptcha';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {first, take} from 'rxjs/operators';
import {ShareDataConsent, SubmitStatus, UserType} from '../../models/dcp-user-type.model';
import {DcpProductUtilitiesService} from '../../services/dcp-product-utilities.service';
import {LeadFormDialogData, LeadService} from '../../services/lead.service';
import {LeadFormCompanyDetailsComponent} from './lead-form-company-details/lead-form-company-details.component';
import {LeadFormContractDetailsComponent} from './lead-form-contract-details/lead-form-contract-details.component';
import {LeadFormPartnerDetailsComponent} from './lead-form-partner-details/lead-form-partner-details.component';
import {
  GoogleCaptchaDataModel,
  LeadFormAddressMap,
  LeadFormAddressModel,
  LeadFormCampaignDataModel,
  LeadFormContractDataModel,
  LeadFormCreator,
  LeadFormCreatorModel,
  LeadFormDataJsonModel,
  LeadFormModel,
  LeadFormUserDataModel
} from './lead-form.model';

RecaptchaComponent.prototype.ngOnDestroy = function() {
  if (this.subscription) {
    this.subscription.unsubscribe();
  }
};

@UntilDestroy()
@Component({
  selector: 'dcp-lead-form',
  templateUrl: './lead-form.component.html',
  styleUrls: ['./lead-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LeadFormComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(NgForm, {static: false}) leadForm;
  @ViewChild(LeadFormCompanyDetailsComponent, {static: false}) leadFormCompanyComponent: LeadFormCompanyDetailsComponent;
  @ViewChild(LeadFormPartnerDetailsComponent, {static: false}) leadFormPartnerComponent: LeadFormPartnerDetailsComponent;
  @ViewChild(LeadFormContractDetailsComponent, {static: false}) leadFormContractComponent: LeadFormContractDetailsComponent;
  @ViewChild('leadForm', {read: NgForm, static: false}) leadFormRef: NgForm;
  @ViewChild('leadForm', {read: ElementRef, static: false}) leadFormEl: ElementRef;
  @ViewChild(SubmitFormButtonDirective, {static: false}) submitButton: SubmitFormButtonDirective;
  @ViewChild('recaptchaRef') recaptchaRef: any;

  image: Image;

  public emailValidationRegexp: RegExp = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  public transactionListCta: TransactionListCta;
  public transactionType: TransactionType;
  public product: Product;
  public user$: Observable<User>;
  public user: LeadFormCreator;
  /** this needs to be an object so isEmployeeLogin can be changed in subcomponents */
  public userType: UserType = {isEmployeeLogin: false};
  public shareDataWithPartnersConsent: DcpConsentTemplateData;
  /** this needs to be an object so isShareDataConsentWarningRed can be changed in subcomponents */
  public shareDataConsent: ShareDataConsent = {
    isShareDataConsentWarningRed: false,
    shouldShareDataConsentWarningBeShown: true,
    saveDcpLeadFormConsent: false,
    showDcpLeadFormConsent: false,
  };
  public showCtaFrom = true;
  public isConsentCheckboxChecked = false;
  public leadFormModel = new LeadFormModel();
  public leadFormContractDataModel = new LeadFormContractDataModel();
  public productDetermination: ProductDetermination;
  /** tracks if the onSubmit function has been called */
  public submitStatus: SubmitStatus = {
    submitted: false,
    isSubmitButtonDisabled: false,
  };
  /** use to show reCaptcha **/
  public siteKey: string;
  public showSuccessMessage = false;
  public response: HttpErrorResponse;
  public sapEmployeeSubject = new BehaviorSubject<boolean>(false);
  public formName: string;
  public leadFormCaptchaChallengePassed: boolean = false;
  public leadFormCaptchaFeatureToggle: boolean = false;
  public shouldShowContractDetailsColumn: boolean = false;
  public showContractDetailsWithCustomerIdOnly: boolean = false;
  public isEmployeeForm: boolean = false;

  public bundleTemplate: DcpBundleTemplate;
  public sapEmployeeSubscription: Subscription = Subscription.EMPTY;
  private dcpKymaConsentSubscription: Subscription = Subscription.EMPTY;
  private dcpKymaUserRoleSubscription: Subscription = Subscription.EMPTY;
  private userAuthSubscription: Subscription = Subscription.EMPTY;
  private userSubscription: Subscription = Subscription.EMPTY;
  private pdpCtaSubscription: Subscription = Subscription.EMPTY;
  private contractDetailsFeatureToggleSubscription: Subscription = Subscription.EMPTY;
  private leadFormCaptchaSubscription: Subscription = Subscription.EMPTY;
  private dialogDataSubscription: Subscription = Subscription.EMPTY;

  constructor(
    public authService: DcpAuthService,
    public userFacade: UserAccountFacade,
    public dcpKymaService: DcpKymaService,
    public ren2: Renderer2,
    public cdRef: ChangeDetectorRef,
    private leadFormService: LeadService,
    private productUtilitiesService: DcpProductUtilitiesService,
    private campaignTrackingService: CampaignTrackingService,
    private crmAccountTrackingService: CrmAccountTrackingService,
    private productPageDecoratorService: ProductPageDecoratorService,
    private togglesService: FeatureTogglesService,
    private configService: ConfigService,
    public countrySelectorService: CountrySelectorService,
    public launchDialogService: LaunchDialogService,
    public authStorageService: AuthStorageService,
    private userService: DcpUserService
  ) {
  }

  @HostListener('document:keydown.escape')
  @HostListener('click', ['$event'])
  handleClickOrEsc(): void {
    this.launchDialogService.closeDialog('Cross click');
  }

  /**
   * Adding defined data object to the array of json objects sent to backend.
   *
   * @param leadFormModel lead form to add defined data to
   * @param jsonDataObject object containing defined data
   * @param jsonName name of json to save data to
   * @private only used in the lead form
   */
  private static setJsonDataObject(leadFormModel: LeadFormModel, jsonDataObject, jsonName: string) {
    const dataObject: LeadFormDataJsonModel = {
      type: jsonName,
      value: JSON.stringify(jsonDataObject),
    };
    if (leadFormModel.data?.length) {
      const existingItemIndex = leadFormModel.data.findIndex(item => item.type === jsonName);
      if (existingItemIndex >= 0) {
        leadFormModel.data.splice(existingItemIndex, 1, dataObject);
      } else {
        leadFormModel.data.push(dataObject);
      }
    } else {
      leadFormModel.data = [dataObject];
    }
  }

  ngOnInit(): void {
    this.siteKey = this.configService.getString('RE_CAPTCHA_SITE_KEY');
    this.user$ = this.userFacade.get();
    this.userSubscription = this.user$.pipe(untilDestroyed(this)).subscribe((u: LeadFormCreator) => {
      this.user = u;
      this.dialogDataSubscription = this.launchDialogService.data$.pipe(untilDestroyed(this)).subscribe((leadFormDialogData: LeadFormDialogData) => {
        this.setDialogData(leadFormDialogData);
        this.fillUserForm(u);
      });
    });
    if (!this.user) {
      this.fillUserForm(this.user);
    }
    this.image = this.productUtilitiesService.getPrimaryImg(this.product);
    if (this.transactionType.code) {
      this.showCtaFrom = !this.transactionType.code.endsWith('RFI');
    }
    this.subscribeToFeatureToggles();
  }

  ngAfterViewInit(): void {
    this.sapEmployeeSubscription.unsubscribe();
    this.sapEmployeeSubscription = this.sapEmployeeSubject.pipe(untilDestroyed(this)).subscribe((userIsEmployee: boolean) => {
      // Makes sure rendering is completed
      setTimeout(() => {
        if (userIsEmployee) {
          this.isEmployeeForm = true;
          this.switchToEmployeeForm();
        }
        this.focusFirstElement('.lead-form-container .modal-close-btn');
      }, 0);
    });
    this.pdpCtaSubscription = this.productPageDecoratorService.getCtaProductDetermination(this.product).pipe(untilDestroyed(this)).subscribe(ctas => {
      this.productDetermination = ctas;
    });
  }

  setDialogData(leadFormDialogData: LeadFormDialogData): void {
    this.transactionListCta = leadFormDialogData?.transactionListCta;
    this.transactionType = leadFormDialogData?.transactionType;
    this.product = leadFormDialogData?.product;
    this.bundleTemplate = leadFormDialogData?.bundleTemplate;
    this.formName = leadFormDialogData?.formName;
  }

  subscribeToFeatureToggles() {
    this.leadFormCaptchaSubscription = this.togglesService.isEnabled(UNLEASH_TOGGLES.leadFormCaptcha).pipe(
      untilDestroyed(this)
    ).subscribe((isEnabled) => {
      this.leadFormCaptchaFeatureToggle = isEnabled;
    });
  }

  ngOnDestroy(): void {
    this.dcpKymaConsentSubscription.unsubscribe();
    this.userAuthSubscription.unsubscribe();
    this.dcpKymaUserRoleSubscription.unsubscribe();
    this.sapEmployeeSubscription.unsubscribe();
    this.userSubscription.unsubscribe();
    this.pdpCtaSubscription.unsubscribe();
    this.contractDetailsFeatureToggleSubscription.unsubscribe();
    this.leadFormCaptchaSubscription.unsubscribe();
    this.dialogDataSubscription.unsubscribe();
    this.leadFormService.leadFormLastOpened = undefined;
  }

  /**
   * Change Field Validators for lastName, email, firstName, companyName.
   */
  switchToEmployeeForm() {
    this.shouldShowContractDetailsColumn = this.showContractDetailsWithCustomerIdOnly ?
        !!this.crmAccountTrackingService.getDataFromSessionStorage(customerIdParameterName) :
        true;
    this.changeFieldValidators(this.leadFormRef, 'lastName');
    this.changeFieldValidators(this.leadFormRef, 'email');
    this.changeFieldValidators(this.leadFormRef, 'firstName');
    this.changeFieldValidators(this.leadFormRef, 'companyName');
    this.setExternalCustomerId();
    this.setAdditionalParameters();
    this.cdRef.detectChanges();
  }

  /**
   * In case there's an external customer id in the user session,
   * set it to the respective leaf form address (LTAC)
   */
  setExternalCustomerId(): void {
    const customerId = this.crmAccountTrackingService.getDataFromSessionStorage(customerIdParameterName);
    if (customerId) {
      this.leadFormContractDataModel.sapCustomerID = customerId;
    }
  }

  /**
   * removing an external customer id from session and clearing it on the model
   * (cases where non-employee customer logs in)
   */
  removeExternalCustomerId(isEmployee: boolean): void {
    if (!isEmployee) {
      this.crmAccountTrackingService.clearData(customerIdParameterName);
      this.leadFormContractDataModel.sapCustomerID = null;
    }
  }

  /**
   * Reading consent for current user, storing it. Filling lead form data for user,
   * their login status and their consent for current product.
   *
   * @param user - current user
   */
  fillUserForm(user: LeadFormCreator): void {
    this.dcpKymaConsentSubscription.unsubscribe();
    this.dcpKymaConsentSubscription = this.dcpKymaService.getConsentTemplate(user, this.product.code)
      .pipe(untilDestroyed(this))
      .subscribe(consentTemplate => {
        this.fillFormLoginData(user, consentTemplate);
        this.shareDataWithPartnersConsent = consentTemplate;
        if (!!consentTemplate.description?.length) {
          this.adaptConsentDescription(consentTemplate);
        }
      }, () => {
        this.fillFormLoginData(user, this.shareDataWithPartnersConsent);
      });
  }

  /**
   * Show consent for anonymous or logged in non-employee user (if consent exists).
   * Prefill form subject for employee, prefill data for non-employee (if email is present).
   *
   * @param user current user
   * @param consentTemplate consent retrieved for current user and current product
   */
  fillFormLoginData(user: LeadFormCreator, consentTemplate: DcpConsentTemplateData) {
    if (user?.email) {
      const isEmployee = this.userService.hasEmployeeRole();

      if (isEmployee) {
        this.sapEmployeeSubject.next(true);
      } else {
        this.removeExternalCustomerId(isEmployee);
        this.updateShareDataConsent(consentTemplate);
        this.sapEmployeeSubject.next(false);
        this.prefillUserData(user);
      }
    } else {
      this.sapEmployeeSubject.next(false);
      this.updateShareDataConsent(consentTemplate);
    }
  }

  updateShareDataConsent(consentTemplate: DcpConsentTemplateData) {
    if (consentTemplate?.description || !consentTemplate) {
      this.shareDataConsent.saveDcpLeadFormConsent = true;
    }
    this.shareDataConsent.showDcpLeadFormConsent = true;
  }

  /**
   * Adapt retrieved from Kyma consent template description:
   * - replace parameter with actual Vendor name (if exists);
   * - change link to privacy statement, so it's opened in the new tab (if exists).
   *
   * @param consentTemplate consent template retrieved from Kyma
   */
  adaptConsentDescription(consentTemplate: DcpConsentTemplateData) {
    if (this.product.vendor?.name && consentTemplate?.description?.includes('{0}')) {
      consentTemplate.description = consentTemplate.description.replace('{0}', this.product.vendor.name);
    }
    if (consentTemplate?.description?.includes('href')) {
      consentTemplate.description = consentTemplate.description.replace('href', 'target=\'_blank\' href');
    }
  }

  /**
   * Changes lead form fields validators.
   * Customized for a sap employee.
   *
   * @param formRef lead form ngForm reference
   * @param field is field name from lead form which should be updated
   */
  changeFieldValidators(formRef: NgForm, field: string): void {
    if (this.leadFormEl?.nativeElement) {
      this.ren2?.removeAttribute(this.leadFormEl.nativeElement.querySelector('[name=' + field + ']'), 'required');
    }
    formRef.form.controls[field].clearValidators();
    if (field === 'email') {
      formRef.form.controls[field].setValidators([Validators.maxLength(255), Validators.pattern(this.emailValidationRegexp)]);
    } else if (field === 'companyName') {
      this.changeCompanyNameValidators(formRef, field);
    } else {
      formRef.form.controls[field].setValidators([Validators.maxLength(255)]);
    }
    formRef.form.controls[field].updateValueAndValidity();
  }

  /**
   * Changes lead form company name validators.
   * Customized for a sap employee.
   *
   * @param formRef lead form ngForm reference
   * @param field is field name from lead form which should be updated
   */
  changeCompanyNameValidators(formRef: NgForm, field: string): void {
    const regexNoOnlyNumbers: string = '(?!^[\\s]*\\d+[\\s]*$)^.+$';
    const regexNotContainsSAP: string = '(?!(\\b|\\W|_)+[Ss][Aa][Pp](\\b|\\W|_)+)^.*$';
    formRef.form.controls[field].setValidators([
      Validators.maxLength(255),
      Validators.required,
      WhitespaceOnlyDirective.whitespaceOnlyValidator,
      Validators.pattern(regexNoOnlyNumbers),
      Validators.pattern(regexNotContainsSAP),
    ]);
  }

  /**
   * Fills default data into the form.
   *
   * @param user Form the user service.
   */
  prefillUserData(user: LeadFormCreatorModel) {
    //  Make sure the leadFormCompanyComponent is fully loaded
    setTimeout(() => {
      if (this.leadFormPartnerComponent) {
        this.leadFormPartnerComponent.prefillDefaultAddress(user);
      }
      if (this.leadFormCompanyComponent) {
        this.leadFormCompanyComponent.prefillDefaultUnitAddress(user);
      }
    }, 0);
  }

  /**
   * Adds missing data to the models that will be sent to the backend.
   *
   * @param leadFormModel Form Data model
   * @param product Product model
   * @param transactionType  current transaction Type
   * @param user logged in user
   * @param leadFormContractData harmony salespersons contract data
   * @param campaignData provided by CampaignTrackingService
   * @param captchaResponseData string containing the captcha response
   * @return leadFormModel with the addresses, productCode, transactionTypeCode and country attributes filled
   */
  fillMissingModelData(
    leadFormModel: LeadFormModel,
    product: Product,
    transactionType: TransactionType,
    user: LeadFormCreator,
    leadFormContractData: LeadFormContractDataModel,
    campaignData: CampaignData,
    captchaResponseData: string,
  ): LeadFormModel {
    leadFormModel.productCode = this.productUtilitiesService.getProductCode(product, this.bundleTemplate);
    leadFormModel.transactionTypeCode = transactionType.code;
    leadFormModel = this.setCreatorInformation(leadFormModel, user);
    leadFormModel.addresses = this.addressesMapToArray(leadFormModel);
    if (campaignData) {
      const leadFormCampaignData: LeadFormCampaignDataModel = {
        type: 'campaign_data',
        value: JSON.stringify(campaignData),
      };
      leadFormModel.data = [leadFormCampaignData];
    }
    this.fillContractData(leadFormModel, leadFormContractData);
    this.fillUserData(leadFormModel);
    this.fillCaptchaTokenData(leadFormModel, captchaResponseData);
    return leadFormModel;
  }

  /**
   * Adds email, first name and last name out of the logged-in user to the LeadFormCreatorModel
   *
   * @param leadFormModel Form Data model
   * @param user currently logged in
   * @return LeadFormCreatorModel filled with email, first name and last name
   */
  setCreatorInformation(leadFormModel: LeadFormModel, user: LeadFormCreator): LeadFormModel {
    if (user?.email) {
      leadFormModel.addressesMap.LC.email = user.email;
      leadFormModel.addressesMap.LC.firstName = user.firstName;
      leadFormModel.addressesMap.LC.lastName = user.lastName;
    } else {
      leadFormModel.addressesMap.LC = Object.assign({}, leadFormModel.addressesMap.LTCP);
    }
    return leadFormModel;
  }

  /**
   * Converts addressesMap to an array and stores it in addresses while setting its addressUsage.
   *
   * @param leadFormModel Form Data model
   * @return array with the values of the leadFormModel.addressesMap attribute that can be placed in 'leadFormModel.addresses'
   */
  addressesMapToArray(leadFormModel: LeadFormModel): LeadFormCreatorModel[] | LeadFormAddressModel[] {
    const addresses: LeadFormCreatorModel[] | LeadFormAddressModel[] = [];
    for (const key in leadFormModel.addressesMap) {
      if (leadFormModel.addressesMap.hasOwnProperty(key)) {
        const address: any = leadFormModel.addressesMap[key];
        address.addressUsage = key;
        addresses.push(address);
      }
    }
    return addresses;
  }

  /**
   * Set null instead empty values at lead from model
   */
  changeEmptyValueToNull(value: string) {
    if (value != null && value.trim() === '') {
      value = null;
    }
    return value;
  }

  changeEmptyValuesToNull(addressesMap: LeadFormAddressMap) {
    addressesMap.LTCP.firstName = this.changeEmptyValueToNull(addressesMap.LTCP.firstName);
    addressesMap.LTCP.lastName = this.changeEmptyValueToNull(addressesMap.LTCP.lastName);
    addressesMap.LTCP.phone = this.changeEmptyValueToNull(addressesMap.LTCP.phone);
    addressesMap.LTCP.email = this.changeEmptyValueToNull(addressesMap.LTCP.email);
    addressesMap.LTAC.line1 = this.changeEmptyValueToNull(addressesMap.LTAC.line1);
  }

  /**
   * Submits the data of the lead form to the server
   */
  async onSubmit(captchaResponseData?: string) {
    this.leadFormModel = this.fillMissingModelData(this.leadFormModel, this.product, this.transactionType, this.user,
      this.leadFormContractDataModel, this.campaignTrackingService.getCampaignData(), captchaResponseData);
    this.submitStatus.submitted = true;
    this.changeEmptyValuesToNull(this.leadFormModel.addressesMap);
    if (this.isConsentValid() && this.leadForm.valid && this.isContractComponentValid()) {
      if (this.leadFormCaptchaFeatureToggle && !this.leadFormCaptchaChallengePassed) {
        await this.recaptchaRef.execute();
        this.leadFormCaptchaChallengePassed = true;
        return;
      }
      this.submitStatus.isSubmitButtonDisabled = true;
      this.leadFormService.postLeadData(this.leadFormModel)
        .pipe(first(), untilDestroyed(this))
        .subscribe((leadId: string) => {
          this.showSuccessMessage = true;
          if (!this.shareDataConsent.shouldShareDataConsentWarningBeShown && this.shareDataConsent.saveDcpLeadFormConsent) {
            this.saveCapturedConsent(leadId);
          }
          this.submitButton.triggerSubmitTrackingCall(
            leadId,
            this.leadFormModel.addressesMap.LTAC.country.isocode,
          );
          this.submitButton.triggerEddlSubmitFormEvent(
            leadId,
            this.leadFormModel.addressesMap.LTAC.country.isocode
          );
          this.campaignTrackingService.clearCampaignData();
        }, (error: HttpErrorResponse) => {
          this.response = this.handleErrorResponse(error);
          this.submitStatus.isSubmitButtonDisabled = false;
          this.submitButton.triggerErrorTrackingCall(error.message);
          this.submitButton.triggerEddlFormErrorEvent(error.message);
        });
    } else {
      this.focusFirstElement('ds-input.ng-invalid input');
    }
  }

  public async reCaptchaSubmit(captchaResponse: string): Promise<void> {
    try {
      await this.onSubmit(captchaResponse);
    } catch (e) {
      this.focusFirstElement('ds-input.ng-invalid input');
    }
  }

  /**
   * Making a Kyma call to save captured by user lead form consent in Hybris (backoffice).
   *
   * @param leadId lead object code of the lead created from submitting the lead form
   */
  saveCapturedConsent(leadId: any): void {
    if (leadId) {
      this.dcpKymaService.saveCapturedConsent(
        this.user,
        this.leadFormModel.addressesMap.LTCP.email,
        leadId,
        this.shareDataWithPartnersConsent,
      );
    }
  }

  /**
   * If data sharing with partners consent is not checked by current user -
   * it should become red.
   * Returns true if consent check warning should not be shown (= consent is checked),
   * returns false and paints consent text in red otherwise.
   * This method is not reached/executed if other mandatory form fields are invalid.
   */
  isConsentValid(): boolean {
    const isConsentInvalid: boolean = this.shareDataConsent.shouldShareDataConsentWarningBeShown
      && this.shareDataConsent.showDcpLeadFormConsent;
    if (isConsentInvalid) {
      this.shareDataConsent.isShareDataConsentWarningRed = true;
    }
    return !isConsentInvalid;
  }

  /**
   * Gets elements by querySelector and sets the focus on the element.
   *
   * @param selector querySelector that matches the invalid elements that should be focused.
   */
  focusFirstElement(selector: string) {
    const invalidControl: any = this.leadFormEl?.nativeElement.querySelector(selector);
    if (invalidControl) {
      invalidControl.focus();
    }
  }

  /**
   * Filters duplicated error messages
   *
   * @param error Response containing an error
   */
  handleErrorResponse(error: HttpErrorResponse): HttpErrorResponse {
    if (error && error.error && error.error.errors) {
      const noDuplicationErrors: Array<HttpErrorResponse> = [];
      for (const err of error.error.errors) {
        if (!noDuplicationErrors.find(e => e.message === err.message)) {
          noDuplicationErrors.push(err);
        }
      }
      error.error.errors = noDuplicationErrors;
    }
    return error;
  }

  /**
   * Defining whether to make consent text red <b>after submit button is clicked</b>.
   *
   * @param checkboxCheckedStatus determines whether changed to 'checked' or not.
   */
  onConsentCheckboxChange(checkboxCheckedStatus: any): void {
    checkboxCheckedStatus.preventDefault();
    this.isConsentCheckboxChecked = !this.isConsentCheckboxChecked;

    if (this.isConsentCheckboxChecked) {
      this.shareDataConsent.shouldShareDataConsentWarningBeShown = false;
      this.shareDataConsent.isShareDataConsentWarningRed = false;
    } else {
      this.shareDataConsent.shouldShareDataConsentWarningBeShown = true;
    }
  }

  /**
   * Fill contract data to the array of json objects if required, together with product data.
   * Fill product data only, if contract data is not required.
   *
   * @param leadFormModel
   * @param leadFormContractData
   * @private
   */
  private fillContractData(leadFormModel: LeadFormModel, leadFormContractData: LeadFormContractDataModel) {
    if (this.shouldShowContractDetailsColumn) {
      this.fillProductData(leadFormModel, leadFormContractData);
    } else {
      this.fillProductData(leadFormModel);
    }
  }

  /**
   * Adding google captcha cToken to the array of json objects sent to backend
   *
   * @param leadFormModel
   * @param captchaResponse
   * @private
   */
  private fillCaptchaTokenData(leadFormModel: LeadFormModel, captchaResponse: string) {
    if (captchaResponse) {
      const captchaDataModel: GoogleCaptchaDataModel = new GoogleCaptchaDataModel(captchaResponse);
      LeadFormComponent.setJsonDataObject(leadFormModel, captchaDataModel, 'captcha_data');
    }
  }

  /**
   * Adding user data object to the array of json objects sent to backend.
   *
   * @param leadFormModel lead form to add contract data to
   * @private only used in the lead form
   */
  private fillUserData(leadFormModel: LeadFormModel) {
    if (this.user?.scimUserId) {
      const userDataModel: LeadFormUserDataModel = new LeadFormUserDataModel(this.user?.scimUserId);
      LeadFormComponent.setJsonDataObject(leadFormModel, userDataModel, 'user_data');
    }
  }

  /**
   * Adding product data object to the array of json objects sent to backend.
   * Filling successful lead placement bundle template of the respective product edition.
   *
   * @param leadFormModel lead form to add contract data to
   * @param leadFormContractData contract data of the lead form
   * @private only used in the lead form
   */
  private fillProductData(leadFormModel: LeadFormModel, leadFormContractData: LeadFormContractDataModel = {}) {
    if (this.product) {
      if (!this.bundleTemplate && this.product.bundleTemplates) {
        this.bundleTemplate = this.product.bundleTemplates.find(bundle =>
          bundle.transactionTypes?.some(bundleTransaction =>
            bundleTransaction.code === this.transactionListCta.code));
      }
      leadFormContractData.materialID = this.bundleTemplate?.supplierAlternativeAID || this.product?.supplierAlternativeAID;
      leadFormContractData.entitlementId = this.productUtilitiesService.getCanonicalId(this.product, this.bundleTemplate)?.entitlementRuleId;
      if (!leadFormContractData.materialID || !leadFormContractData.entitlementId) {
        leadFormContractData.materialID = this.fetchHeroSectionCtaProductEditionData('materialNumber');
        leadFormContractData.entitlementId = this.fetchHeroSectionCtaProductEditionData('id');
      }
      if (!leadFormContractData.materialID) {
        leadFormContractData.materialID = this.fetchOnlyEditionAttribute('materialNumber');
        leadFormContractData.entitlementId = this.fetchOnlyEditionAttribute('id');
      }
    }
    LeadFormComponent.setJsonDataObject(leadFormModel, leadFormContractData, 'contract_data');
  }

  private isContractComponentValid() {
    return this.leadFormContractComponent ? (this.leadFormContractComponent.contractDetailsForm ? this.leadFormContractComponent.contractDetailsForm.valid : true) : true;
  }

  /**
   * Get specified entitlement field having CTA object with LEAD_BUY code
   *
   * @param identifier
   */
  fetchHeroSectionCtaProductEditionData(identifier: string) {
    const identifierValue = this.productDetermination?.entitlements?.filter(entitlement =>
      entitlement.callToActions.filter(cta =>
        cta.code === this.transactionListCta.code
      ).length > 0
    );
    return identifierValue && identifierValue.length > 0 ? identifierValue[0][identifier] : null;
  }

  fetchOnlyEditionAttribute(identifier: string): string {
    if (this.productDetermination?.entitlements?.length) {
      return this.productDetermination.entitlements[0][identifier];
    }
  }

  /**
   * Populates the company name in the company details form from the session storage
   */
  setCompanyName() {
    this.leadFormModel.addressesMap.LTAC.companyName =
      this.crmAccountTrackingService.getDataFromSessionStorage(companyNameParameterName);
  }

  /**
   * Populates the street in the company details form from the session storage
   * if street and line2 are in the session storage then the street should be populated with street + line2
   * if line2 is null then street should be just 'street' from session storage
   */
  setStreetLine2() {
    const street = this.crmAccountTrackingService.getDataFromSessionStorage(streetParameterName);
    const line2 = this.crmAccountTrackingService.getDataFromSessionStorage(streetLine2ParameterName);
    if (street != null && line2 != null) {
      this.leadFormModel.addressesMap.LTAC.line1 = street + ' ' + line2;
    } else if (street != null) {
      this.leadFormModel.addressesMap.LTAC.line1 = street;
    } else {
      this.leadFormModel.addressesMap.LTAC.line1 = null;
    }
  }

  /**
   * Populates the city in the company details form from the session storage
   */
  setCity() {
    this.leadFormModel.addressesMap.LTAC.town =
      this.crmAccountTrackingService.getDataFromSessionStorage(cityParameterName);
  }

  /**
   * Populates the postal code in the company details form from the session storage
   */
  setPostalCode() {
    this.leadFormModel.addressesMap.LTAC.postalCode =
      this.crmAccountTrackingService.getDataFromSessionStorage(postalCodeParameterName);
  }

  /**
   * Populates the country in the company details form based on the country code from session storage
   */
  setCountryCode() {
    const countryIsoCode = this.crmAccountTrackingService.getDataFromSessionStorage(countryParameterName);
    if (countryIsoCode) {
      this.countrySelectorService.getCountries()
        .pipe(
          take(1), untilDestroyed(this)
        )
        .subscribe((countries) => {
          this.leadFormCompanyComponent.setFormCountry(countries.find(country => country.isocode === countryIsoCode));
          this.leadFormCompanyComponent.setRegionsObservable();
        });
    }
  }

  /**
   * Populates the company name and address data in the company details form based on the data in the session storage
   */
  setAdditionalParameters() {
    this.setCountryCode();
    this.setCompanyName();
    this.setStreetLine2();
    this.setCity();
    this.setPostalCode();
  }
}
